import moment from 'moment';
import { CommutingTrip, CommutingTripOccurrence, CommutingTripPotentialOccurrence, CommutingTripSummary, Place, UserPlace, daysMap } from '../models';
import { HttpService } from './http';
import { UserService, parseUserReferenceTrip, toBackendTripDirection, toTripDirection } from './user';
export class CommutingTripService {
  static async createCommutingTrip({
    employeeId: employee,
    title,
    home,
    work,
    bicyclePortions,
    isWorkToHome: display_reverse_order
  }) {
    if (!UserService.currentUser) throw new Error('user not connected');
    try {
      const data = await HttpService.post('v1', `/commuting_trips`, [], [], JSON.stringify({
        employee,
        title,
        home: home.id,
        work: work.id,
        bicycle_portions: bicyclePortions.map(({
          start,
          end,
          order: commuting_trip_order
        }) => ({
          user_place_start: start.id,
          user_place_end: end.id,
          commuting_trip_order
        })),
        display_reverse_order
      }));
      return parseCommutingTrip(data);
    } catch (err) {
      console.error('[CommutingTripService][createCommutingTrip]', err);
      throw new Error('commuting trip not created');
    }
  }
  static async updateCommutingTrip({
    id,
    home,
    work
  }, {
    employeeId: employee,
    title,
    bicyclePortions
  }) {
    if (!UserService.currentUser) throw new Error('user not connected');
    try {
      const data = await HttpService.patch('v1', `/commuting_trips/${id}`, [], [], JSON.stringify({
        employee,
        title,
        home: home.id,
        work: work.id,
        bicycle_portions: bicyclePortions.map(({
          start,
          end,
          order: commuting_trip_order
        }) => ({
          user_place_start: start.id,
          user_place_end: end.id,
          commuting_trip_order
        }))
      }));
      return parseCommutingTrip(data);
    } catch (err) {
      console.error('[CommutingTripService][updateCommutingTrip]', err);
      throw new Error('commuting trip not updated');
    }
  }
  static async getCommutingTrips({
    employeeId
  }) {
    if (!UserService.currentUser) throw new Error('user not connected');
    try {
      const {
        results
      } = await HttpService.get('v1', `/commuting_trips`, [{
        key: 'page',
        value: 1
      }, {
        key: 'page_size',
        value: 100
      }, {
        key: 'employee_id',
        value: employeeId
      }, {
        key: 'enabled',
        value: true
      }, {
        key: 'query',
        value: '{id,title,employee,display_reverse_order,home,home_address,home_point,work,work_address,work_point,bicycle_portions{id,user_place_start,user_place_end,geo_start,geo_end,geo_start_title,geo_end_title,distance_in_meters_start_end,distance_in_meters_end_start,commuting_trip_order}}'
      }]);
      return results.map(parseCommutingTripSummary);
    } catch (err) {
      console.error('[CommutingTripService][getCommutingTrips]', err);
      throw err;
    }
  }
  static async getCommutingTrip(id) {
    if (!UserService.currentUser) throw new Error('user not connected');
    try {
      const data = await HttpService.get('v1', `/commuting_trips/${id}`);
      return parseCommutingTrip(data);
    } catch (err) {
      console.error('[CommutingTripService][getCommutingTrip]', err);
      throw err;
    }
  }
  static async removeCommutingTrip(id) {
    if (!UserService.currentUser) throw new Error('user not connected');
    try {
      await HttpService.delete('v1', `/commuting_trips/${id}`);
      return true;
    } catch (err) {
      console.error('[CommutingTripService][removeCommutingTrip]', err);
      throw err;
    }
  }
  static async getOccurrences({
    employeeId,
    page,
    pageSize,
    orderBy,
    order,
    period,
    dateStart,
    dateEnd
  }) {
    try {
      const params = [{
        key: 'employee_id',
        value: employeeId
      }, {
        key: 'page',
        value: page
      }, {
        key: 'page_size',
        value: pageSize
      }];
      if (!UserService.currentUser) throw new Error('user not connected');
      if (period === 'custom' && (dateStart === undefined || dateEnd === undefined)) throw new Error('dateStart & dateEnd are required in custom mode');
      if (period !== 'custom' && (dateStart !== undefined || dateEnd !== undefined)) throw new Error('dateStart & dateEnd are incompatible with this period mode');
      if (orderBy && order) {
        let value;
        if (orderBy === 'id') value = 'id';
        if (value) {
          if (order === 'desc') value = `-${value}`;
          params.push({
            key: 'ordering',
            value
          });
        }
      }
      if (period) params.push({
        key: 'period',
        value: period
      });
      if (dateStart) params.push({
        key: 'date_start',
        value: dateStart.format('YYYY-MM-DD')
      });
      if (dateEnd) params.push({
        key: 'date_end',
        value: dateEnd.format('YYYY-MM-DD')
      });
      const {
        count,
        next,
        results
      } = await HttpService.get('v1', `/commuting_trip_occurrences`, params);
      return {
        count,
        next,
        occurrences: results.map(parseCommutingTripOccurrence)
      };
    } catch (err) {
      console.error('[CommutingTripService][getOccurrences]', err);
      throw err;
    }
  }
  static async addOccurrence(commutingTripId, {
    date,
    direction
  }) {
    try {
      if (!UserService.currentUser) throw new Error('user not connected');
      const data = await HttpService.post('v1', `/commuting_trip_occurrences`, [], [], JSON.stringify({
        commuting_trip: commutingTripId,
        date,
        direction: toBackendTripDirection(direction)
      }));
      return parseCommutingTripOccurrence(data);
    } catch (err) {
      console.error('[CommutingTripService][addOccurrence]', err);
      throw typeof err === 'object' && err && 'non_field_errors' in err && Array.isArray(err.non_field_errors) ? new Error(err.non_field_errors[0]) : err;
    }
  }
  static async updateOccurrence(id, {
    excluded
  }) {
    try {
      if (!UserService.currentUser) throw new Error('user not connected');
      const data = await HttpService.patch('v1', `/commuting_trip_occurrences/${id}`, [], [], JSON.stringify({
        manually_excluded: excluded
      }));
      return parseCommutingTripOccurrence(data);
    } catch (err) {
      console.error('[CommutingTripService][updateOccurrence]', err);
      throw err;
    }
  }
  static async getPotentialOccurrences({
    fmdConfig,
    employeeId,
    page,
    pageSize,
    orderBy,
    order,
    period,
    dateStart,
    dateEnd
  }) {
    try {
      const params = [{
        key: 'page',
        value: page
      }, {
        key: 'page_size',
        value: pageSize
      }];
      if (!UserService.currentUser) throw new Error('user not connected');
      if (period === 'custom' && (dateStart === undefined || dateEnd === undefined)) throw new Error('dateStart & dateEnd are required in custom mode');
      if (period !== 'custom' && (dateStart !== undefined || dateEnd !== undefined)) throw new Error('dateStart & dateEnd are incompatible with this period mode');
      if (orderBy && order) {
        let value;
        if (orderBy === 'id') value = 'id';
        if (orderBy === 'date') value = 'date';
        if (value) {
          if (order === 'desc') value = `-${value}`;
          params.push({
            key: 'ordering',
            value
          });
        }
      }
      if (period) params.push({
        key: 'period',
        value: period
      });
      if (dateStart) params.push({
        key: 'date_start',
        value: dateStart.format('YYYY-MM-DD')
      });
      if (dateEnd) params.push({
        key: 'date_end',
        value: dateEnd.format('YYYY-MM-DD')
      });
      const {
        count,
        next,
        results
      } = await HttpService.get('v5', `/employees/${employeeId}/reference_trip_occurrences`, params);
      return {
        count,
        next,
        occurrences: results.map(parseCommutingTripPotentialOccurrence).filter(({
          date
        }) => {
          const day = daysMap[date.get('day')];
          return fmdConfig.compensableDays[day];
        })
      };
    } catch (err) {
      console.error('[CommutingTripService][getPotentialOccurrences]', err);
      throw err;
    }
  }
}
function parseCommutingTripSummary({
  id,
  employee,
  title,
  home: homeId,
  home_address,
  home_point,
  work: workId,
  work_address,
  work_point,
  bicycle_portions,
  display_reverse_order: isWorkToHome
}) {
  const portions = (bicycle_portions || []).sort((a, b) => a.commuting_trip_order - b.commuting_trip_order).map(({
    id,
    user_place_start,
    geo_start,
    geo_start_title,
    user_place_end,
    geo_end,
    geo_end_title,
    distance_in_meters_start_end,
    distance_in_meters_end_start
  }) => {
    const portionStart = new UserPlace(user_place_start, 'other', geo_start_title, geo_start, geo_start_title, moment(), undefined);
    const portionEnd = new UserPlace(user_place_end, 'other', geo_end_title, geo_end, geo_end_title, moment(), undefined);
    return isWorkToHome ? {
      id,
      start: portionEnd,
      end: portionStart,
      distance: distance_in_meters_end_start
    } : {
      id,
      start: portionStart,
      end: portionEnd,
      distance: distance_in_meters_start_end
    };
  });
  if (isWorkToHome) portions.reverse();
  const start = new Place(homeId, home_point, {
    primaryText: home_address
  });
  const end = new Place(workId, work_point, {
    primaryText: work_address
  });
  return new CommutingTripSummary(id, employee, title || `Trajet vélotaf ${id}`, isWorkToHome ? end : start, isWorkToHome ? start : end, portions, isWorkToHome);
}
function parseCommutingTrip({
  id,
  employee,
  title,
  home: homeId,
  home_address,
  home_point,
  work: workId,
  work_address,
  work_point,
  bicycle_portions,
  display_reverse_order: isWorkToHome
}) {
  const bicyclePortions = (bicycle_portions || []).sort((a, b) => a.commuting_trip_order - b.commuting_trip_order);
  if (isWorkToHome) bicyclePortions.reverse();
  const start = new Place(homeId, home_point, {
    primaryText: home_address
  });
  const end = new Place(workId, work_point, {
    primaryText: work_address
  });
  return new CommutingTrip(id, employee, title || `Trajet vélotaf ${id}`, isWorkToHome ? end : start, isWorkToHome ? start : end, bicyclePortions.map(({
    id,
    user_place_start,
    geo_start,
    geo_start_title,
    user_place_end,
    geo_end,
    geo_end_title,
    geometry_start_end,
    geometry_end_start,
    distance_in_meters_start_end,
    distance_in_meters_end_start
  }) => {
    const portionStart = new UserPlace(user_place_start, 'other', geo_start_title, geo_start, geo_start_title, moment(), undefined);
    const portionEnd = new UserPlace(user_place_end, 'other', geo_end_title, geo_end, geo_end_title, moment(), undefined);
    return isWorkToHome ? {
      id,
      start: portionEnd,
      end: portionStart,
      geometry: geometry_end_start ?? null,
      distance: distance_in_meters_end_start
    } : {
      id,
      start: portionStart,
      end: portionEnd,
      geometry: geometry_start_end ?? null,
      distance: distance_in_meters_start_end
    };
  }), isWorkToHome);
}
export function parseCommutingTripOccurrence({
  id,
  commuting_trip,
  date,
  direction,
  certified,
  compensated,
  manually_excluded
}) {
  return new CommutingTripOccurrence(id, commuting_trip, moment(date), date, toTripDirection(direction), certified === 'CERTIFIED' || certified === 'CERTIFIED_MANUALLY_FLAGGED_BY_USER', certified === 'CERTIFIED_MANUALLY_FLAGGED_BY_USER' || certified === 'NOT_CERTIFIED_MANUAL_DECLARATION', compensated || false, manually_excluded || false);
}
export function parseCommutingTripPotentialOccurrence({
  id,
  date,
  direction,
  candidate,
  certified_by_geovelo,
  manually_created,
  enabled,
  user_reference_trip,
  user_trace
}) {
  return new CommutingTripPotentialOccurrence(id, moment(date), date, toTripDirection(direction), candidate, certified_by_geovelo, manually_created, enabled, user_reference_trip ? parseUserReferenceTrip(user_reference_trip) : null, user_reference_trip?.commuting_trip ? parseCommutingTrip(user_reference_trip.commuting_trip) : null, user_trace);
}